import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import instrument from '../instrument/u-cognitive-instrument.v0.2.json';
import officialLogo from './assets/3Fold_Labs_Logo.png';
import MemberPortal from './MemberPortal.jsx';
import { buildEvidencePackage, constructSpecs, ensureAgentSetup, portableAgentPackage } from './cognitiveEngine.js';
import { brainStore, readConsent } from './brainStore.js';
import { AssessmentWatermark, CompletionCelebration, ExperienceCanvas } from './ExperienceCanvas.jsx';

gsap.registerPlugin(ScrollTrigger);

const STORAGE_KEY = 'uos.cognitive-wizard.v0.2';
const SUBJECT_KEY = 'uos.cognitive-wizard.subject.v0.2';
const SIDECAR_KEY = 'uos.cognitive.package.v0.2';
const ACCOUNT_PATH = '/account';
const APP_VERSION = '2026-09-02-member-portal-v1';
const actionNames = { mirror: 'MATCH', complement: 'EXPAND', counterbalance: 'CHECK' };
const brainStatusLabels = { 'not-configured': 'Local preview', 'local-only': 'Local only', connecting: 'Saving', synced: 'Saved', error: 'Save needs attention' };

function readState() {
  if (!readConsent()) return { answers: {}, reviews: {}, ruleOverrides: {} };
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { answers: {}, reviews: {}, ruleOverrides: {} };
  } catch {
    return { answers: {}, reviews: {}, ruleOverrides: {} };
  }
}

function localSubjectId() {
  let value = localStorage.getItem(SUBJECT_KEY);
  if (!value) {
    value = `local-${crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36)}`;
    localStorage.setItem(SUBJECT_KEY, value);
  }
  return value;
}

function answerFrom(state, questionId) {
  return state.answers[questionId] || { selected: [], ownWords: '' };
}

function wordCount(value) {
  const clean = String(value || '').trim();
  return clean ? clean.split(/\s+/).length : 0;
}

export default function App() {
  const app = useRef();
  const questionShell = useRef();
  const lenisRef = useRef();
  const resultsAnimated = useRef(false);
  const previewSubjectId = useRef(`preview-${crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36)}`);
  const [state, setState] = useState(readState);
  const [routePath, setRoutePath] = useState(() => window.location.pathname.replace(/\/+$/, '') || '/');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [hoveredAnswer, setHoveredAnswer] = useState(null);
  const [exportPackage, setExportPackage] = useState(null);
  const [previewOnly, setPreviewOnly] = useState(false);
  const [consent, setConsent] = useState(readConsent);
  const [brainStatus, setBrainStatus] = useState(brainStore.getStatus());
  const question = instrument.questions[currentIndex];
  const answer = answerFrom(state, question.id);
  const accountRoute = routePath === ACCOUNT_PATH;
  const portalRoute = ['/dashboard', '/report', '/profile'].includes(routePath);

  async function reloadIfOutdated() {
    try {
      const response = await fetch(`/app-version.json?${Date.now()}`, { cache: 'no-store' });
      const deployed = await response.json();
      if (deployed.version && deployed.version !== APP_VERSION) {
        window.location.reload();
        return true;
      }
    } catch {
      // A temporary network failure should never block the assessment.
    }
    return false;
  }

  const answeredFlags = useMemo(
    () => instrument.questions.map(item => answerFrom(state, item.id).selected.length > 0),
    [state]
  );
  const completed = answeredFlags.filter(Boolean).length;

  useEffect(() => {
    if (consent) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      brainStore.queueDraft(instrument, state);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SIDECAR_KEY);
    }
  }, [state, consent]);

  useEffect(() => brainStore.subscribe(setBrainStatus), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsStoredResult = params.get('results') === '1';
    if (!wantsStoredResult) return;
    let active = true;
    (async () => {
      let storedPackage = null;
      if (readConsent()) {
        try {
          storedPackage = JSON.parse(localStorage.getItem(SIDECAR_KEY));
        } catch {
          storedPackage = null;
        }
      } else {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(SIDECAR_KEY);
      }
      if (storedPackage && params.get('claim') === '1') {
        await brainStore.claimLocalResult(instrument, readState(), storedPackage);
      }
      if (!storedPackage) {
        try { storedPackage = await brainStore.latestModel(); } catch (error) {
          console.error('Stored account result could not be loaded', error);
        }
      }
      if (active && storedPackage) {
        setStarted(false);
        setPreviewOnly(false);
        setExportPackage(ensureAgentSetup(instrument, storedPackage));
      }
      if (active) window.history.replaceState({}, '', '/');
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const onPopState = () => setRoutePath(window.location.pathname.replace(/\/+$/, '') || '/');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') reloadIfOutdated();
    };
    window.addEventListener('focus', checkWhenVisible);
    document.addEventListener('visibilitychange', checkWhenVisible);
    reloadIfOutdated();
    return () => {
      window.removeEventListener('focus', checkWhenVisible);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, []);

  useEffect(() => {
    if (started && !exportPackage && !accountRoute) lenisRef.current?.stop();
    else lenisRef.current?.start();
  }, [started, exportPackage, accountRoute]);

  useEffect(() => {
    if (!completing) return undefined;
    const timer = window.setTimeout(() => {
      setCompleting(false);
      assembleModel(state, false, true);
    }, 2700);
    return () => window.clearTimeout(timer);
  }, [completing, state]);

  useLayoutEffect(() => {
    const lenis = new Lenis({
      smoothWheel: true,
      lerp: 0.075,
      wheelMultiplier: 0.84,
      anchors: true
    });
    lenisRef.current = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const tick = time => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    const context = gsap.context(() => {
      gsap.set('.hero-eyebrow, .hero-title > span, .hero-copy, .enter-lab, .hero-promise', { y: 34, autoAlpha: 0 });
      gsap.timeline({ defaults: { ease: 'power4.out' } })
        .to('.hero-eyebrow, .hero-title > span', { y: 0, autoAlpha: 1, duration: 0.78, stagger: 0.08 })
        .to('.hero-copy, .enter-lab, .hero-promise', { y: 0, autoAlpha: 1, duration: 0.58, stagger: 0.06 }, '-=.42');

      gsap.to('.hero-panel', {
        yPercent: -12,
        autoAlpha: 0,
        ease: 'power2.in',
        scrollTrigger: { trigger: '.hero', start: '42% top', end: '82% top', scrub: 1 }
      });
    }, app);

    return () => {
      context.revert();
      gsap.ticker.remove(tick);
      lenis.destroy();
    };
  }, []);

  useLayoutEffect(() => {
    if (!started || !questionShell.current) return undefined;
    const context = gsap.context(() => {
      gsap.timeline()
        .fromTo('.question-copy',
          { clipPath: 'inset(0 0 100% 0)', y: 46 },
          { clipPath: 'inset(0 0 0% 0)', y: 0, duration: .58, ease: 'power4.out' },
          0
        )
        .fromTo('.answer',
          { y: 48, rotationX: 8, scale: .97, autoAlpha: 0 },
          { y: 0, rotationX: 0, scale: 1, autoAlpha: 1, duration: .5, stagger: .055, ease: 'power4.out' },
          .08
        )
        .fromTo('.own-words',
          { y: 38, scale: .985, autoAlpha: 0 },
          { y: 0, scale: 1, autoAlpha: 1, duration: .48, ease: 'power4.out' },
          .22
        );
    }, questionShell);
    return () => context.revert();
  }, [currentIndex, started]);

  useLayoutEffect(() => {
    if (!exportPackage || resultsAnimated.current) return undefined;
    resultsAnimated.current = true;
    const context = gsap.context(() => {
      gsap.set('.results-kicker, .results-head p, .results-head h2, .construct, .behavior-plan, .export-dock', { y: 58, autoAlpha: 0 });
      gsap.timeline({ defaults: { ease: 'power4.out' } })
        .to('.results-kicker', { y: 0, autoAlpha: 1, duration: .38 })
        .to('.results-head p, .results-head h2', { y: 0, autoAlpha: 1, duration: .58, stagger: .07 }, '-=.18')
        .to('.construct', { y: 0, autoAlpha: 1, duration: .54, stagger: .08 }, '-=.3')
        .to('.behavior-plan, .export-dock', { y: 0, autoAlpha: 1, duration: .48, stagger: .08 }, '-=.22');
    }, app);
    return () => context.revert();
  }, [exportPackage]);

  useEffect(() => {
    const onKey = event => {
      if (event.target.matches('textarea, input')) return;
      if (/^[a-d]$/i.test(event.key)) toggleOption(event.key.toUpperCase());
      if (event.key === 'ArrowLeft') moveTo(currentIndex - 1, -1);
      if (event.key === 'ArrowRight') nextQuestion();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function patchAnswer(questionId, patch) {
    setState(current => ({
      ...current,
      answers: {
        ...current.answers,
        [questionId]: { ...answerFrom(current, questionId), ...patch }
      }
    }));
  }

  function toggleOption(id, target) {
    const selected = answer.selected.includes(id)
      ? answer.selected.filter(value => value !== id)
      : [...answer.selected, id];
    patchAnswer(question.id, { selected });
    if (target) {
      const reaction = target.querySelectorAll('strong, i');
      gsap.timeline()
        .to(reaction, { scale: .94, duration: .08, ease: 'power1.in' })
        .to(reaction, { scale: 1, duration: .32, ease: 'power3.out', clearProps: 'scale' });
    }
  }

  function updateOwnWords(value) {
    let selected = answer.selected.filter(id => id !== 'E');
    if (value.trim()) selected = [...selected, 'E'];
    patchAnswer(question.id, { ownWords: value, selected });
  }

  async function enterLab() {
    if (await reloadIfOutdated()) return;
    resultsAnimated.current = false;
    setPreviewOnly(false);
    setStarted(true);
    setExportPackage(null);
    if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
    setRoutePath('/');
    lenisRef.current?.scrollTo(0, { immediate: true });
  }

  function goToAccount() {
    window.location.assign(ACCOUNT_PATH);
  }

  function navigatePortal(path) {
    if (window.location.pathname !== path) window.history.pushState({}, '', path);
    setRoutePath(path);
    requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'instant' }));
  }

  function moveTo(index, direction = 1) {
    if (transitioning || index < 0 || index >= instrument.questions.length || index === currentIndex) return;
    setTransitioning(true);
    gsap.to(questionShell.current, {
      x: direction * -68,
      y: -18,
      z: -90,
      rotationY: direction * -5,
      scale: .975,
      filter: 'blur(8px)',
      autoAlpha: 0,
      duration: .28,
      ease: 'power4.in',
      onComplete: () => {
        setCurrentIndex(index);
        setTransitioning(false);
        gsap.set(questionShell.current, { clearProps: 'transform,opacity,visibility,filter' });
      }
    });
  }

  async function nextQuestion() {
    if (currentIndex < instrument.questions.length - 1) {
      moveTo(currentIndex + 1, 1);
      return;
    }
    if (completed === 0) {
      assembleModel();
      return;
    }
    if (await reloadIfOutdated()) return;
    setCompleting(true);
  }

  function returnToQuestions() {
    resultsAnimated.current = false;
    setPreviewOnly(false);
    setExportPackage(null);
    setStarted(true);
    setCurrentIndex(0);
    if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
    setRoutePath('/');
    requestAnimationFrame(() => lenisRef.current?.scrollTo(0, { immediate: true }));
  }

  function viewStoredResult() {
    const stored = readState();
    assembleModel(stored, true, false);
    setStarted(false);
    if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
    setRoutePath('/');
  }

  function assembleModel(nextState = state, scroll = true, asPreview = false) {
    const generatedAt = new Date().toISOString();
    const subjectId = consent ? localSubjectId() : previewSubjectId.current;
    const pkg = buildEvidencePackage(instrument, nextState, { subjectId, generatedAt });
    if (consent) localStorage.setItem(SIDECAR_KEY, JSON.stringify(pkg));
    else localStorage.removeItem(SIDECAR_KEY);
    setPreviewOnly(asPreview);
    setExportPackage(pkg);
    brainStore.saveModel(instrument, nextState, pkg);
    requestAnimationFrame(() => lenisRef.current?.scrollTo('#results', { duration: scroll ? 1.2 : 0, immediate: !scroll }));
  }

  function reviewConstruct(constructId, review) {
    const nextState = { ...state, reviews: { ...state.reviews, [constructId]: review } };
    setState(nextState);
    assembleModel(nextState, false);
    const confidence = exportPackage?.cognitiveModel.constructs.find(item => item.constructId === constructId)?.estimate.confidence;
    brainStore.event('construct_reviewed', { constructId, review, confidence });
  }

  function toggleRuleOverride(rule) {
    const disabled = state.ruleOverrides?.[rule.ruleId] === 'disabled';
    const nextOverrides = { ...(state.ruleOverrides || {}) };
    if (disabled) delete nextOverrides[rule.ruleId];
    else nextOverrides[rule.ruleId] = 'disabled';
    const nextState = { ...state, ruleOverrides: nextOverrides };
    setState(nextState);
    assembleModel(nextState, false);
    brainStore.event('adapter_rule_override', {
      ruleId: rule.ruleId,
      mode: rule.mode,
      override: disabled ? 'enabled' : 'disabled',
      confidence: rule.basis.confidence
    });
  }

  function clearDraft() {
    if (!confirm('Clear every answer and local hypothesis review?')) return;
    const blank = { answers: {}, reviews: {}, ruleOverrides: {} };
    setState(blank);
    setCurrentIndex(0);
    setStarted(true);
    setExportPackage(null);
    setPreviewOnly(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SIDECAR_KEY);
    localStorage.removeItem(SUBJECT_KEY);
    brainStore.abandonSession();
  }

  function changeConsent(granted) {
    setConsent(granted);
    brainStore.setConsent(granted, instrument);
    if (granted) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      if (exportPackage) localStorage.setItem(SIDECAR_KEY, JSON.stringify(exportPackage));
      brainStore.queueDraft(instrument, state);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SIDECAR_KEY);
      localStorage.removeItem(SUBJECT_KEY);
    }
  }

  function savePreviewAndContinue() {
    if (!consent || !exportPackage) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    localStorage.setItem(SIDECAR_KEY, JSON.stringify(exportPackage));
    goToAccount();
  }

  async function copyPackage() {
    await navigator.clipboard.writeText(exportPackage.agentSetup.installText);
  }

  function downloadPackage() {
    const blob = new Blob([JSON.stringify(portableAgentPackage(exportPackage), null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = '3fold-ai-workstyle.json';
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  if (portalRoute) return <MemberPortal routePath={routePath} navigate={navigatePortal} />;

  return (
    <div className={`app ${started && !exportPackage && !accountRoute ? 'questionnaire-active' : ''} ${accountRoute ? 'account-active' : ''} ${completing ? 'completion-active' : ''}`} ref={app}>
      <ExperienceCanvas stage={accountRoute ? 'account' : exportPackage ? 'results' : started ? 'assessment' : 'hero'} questionIndex={currentIndex} />
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="3Fold Labs" onClick={event => {
          event.preventDefault();
          setStarted(false);
          setExportPackage(null);
          if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
          setRoutePath('/');
          requestAnimationFrame(() => lenisRef.current?.scrollTo(0, { immediate: true }));
        }}>
          <img src={officialLogo} alt="3Fold Labs" />
          <span>PERSONAL AI SETUP / BETA</span>
        </a>
        <div className="header-tools">
          <div className="header-progress"><span>{String(currentIndex + 1).padStart(2, '0')} / 25</span><i><b style={{ transform: `scaleX(${(currentIndex + 1) / 25})` }} /></i></div>
          <button type="button" onClick={clearDraft}>RESET</button>
          <button className="account-link" type="button" onClick={goToAccount}>{accountRoute ? 'ACCOUNT' : 'SIGN IN'}</button>
          <div className={`header-state ${brainStatus}`} title={brainStatusLabels[brainStatus]} aria-label={brainStatusLabels[brainStatus]}><i /></div>
        </div>
      </header>

      <main>
        {!accountRoute && !started && <section className="hero" id="top">
          <div className="hero-panel">
            <div className="hero-eyebrow"><span>3Fold Labs</span><b>PERSONAL AI, SET UP FOR YOU</b></div>
            <h1 className="hero-title">
              <span>Get more out</span>
              <span>of every AI</span>
              <span className="gold">you use.</span>
            </h1>
            <p className="hero-copy">Answer 25 quick questions and get a personal AI setup built around how you understand, decide, and get things done. Take it to ChatGPT, Claude, Gemini, or any AI you choose.</p>
            <div className="hero-actions">
              <button className="enter-lab" type="button" onClick={enterLab}><span>BUILD MY AI SETUP</span><b>↘</b></button>
              <div className="hero-promise">25 QUESTIONS.<br />ABOUT 6 MINUTES.</div>
            </div>
            <div className="data-consent">
              <label>
                <input type="checkbox" checked={consent} onChange={event => changeConsent(event.target.checked)} />
                <span><b>Save my answers securely.</b> Let 3Fold Labs store my responses—including anything I write—to build my result and improve this product.</span>
              </label>
              <details>
                <summary>What gets saved?</summary>
                <p>Your A–E answers, own words, completion activity, anonymous account ID, model output, and campaign source. No name or email is attached unless you later create or connect an account. This model is experimental, not diagnostic.</p>
              </details>
            </div>
          </div>
        </section>}

        {!accountRoute && started && !exportPackage && <section className="assessment" id="assessment">
          <AssessmentWatermark questionIndex={currentIndex} activeAnswer={hoveredAnswer} />
          <div className="assessment-label"><span>YOUR AI WORKSTYLE</span><span>{question.domain.toUpperCase()}</span></div>

          <article className="question-shell" ref={questionShell}>
            <div className="question-copy">
              <p className="micro-label">SELECT EVERY ANSWER THAT FITS</p>
              <h2>{question.prompt}</h2>
            </div>
            <div className="answer-grid">
              {question.options.map(option => (
                <button
                  className={answer.selected.includes(option.id) ? 'answer selected' : 'answer'}
                  type="button"
                  key={option.id}
                  onMouseEnter={() => setHoveredAnswer(option.id)}
                  onMouseLeave={() => setHoveredAnswer(null)}
                  onFocus={() => setHoveredAnswer(option.id)}
                  onBlur={() => setHoveredAnswer(null)}
                  onClick={event => toggleOption(option.id, event.currentTarget)}
                  aria-pressed={answer.selected.includes(option.id)}
                >
                  <span>{option.id}</span>
                  <strong>{option.text}</strong>
                  <i>{answer.selected.includes(option.id) ? '✓' : '+'}</i>
                </button>
              ))}
            </div>

            <section className={answer.ownWords.trim() ? 'own-words active' : 'own-words'}>
              <div className="own-words-copy">
                <span>E / SAY IT YOUR WAY</span>
                <strong>Your words make the result more useful.</strong>
                <i>OPTIONAL</i>
              </div>
              <div className="writing-surface">
                <textarea
                  value={answer.ownWords}
                  onChange={event => updateOwnWords(event.target.value)}
                  placeholder="What would you add or say differently?"
                  aria-label={`Question ${currentIndex + 1} in your own words`}
                />
                <span>{wordCount(answer.ownWords)} WORDS</span>
              </div>
            </section>

            <footer className="question-footer">
              <span>{answer.selected.length} CHOICE{answer.selected.length === 1 ? '' : 'S'} SELECTED</span>
              <div>
                <button className="previous" type="button" disabled={currentIndex === 0 || transitioning} onClick={() => moveTo(currentIndex - 1, -1)}>← PREVIOUS</button>
                <button type="button" disabled={transitioning} onClick={nextQuestion}>{currentIndex === 24 ? 'BUILD MY WORKSTYLE ↗' : 'NEXT QUESTION →'}</button>
              </div>
            </footer>
          </article>
        </section>}

        {!accountRoute && exportPackage && (
          <section className="results" id="results">
            <div className="results-kicker"><span>OUTPUT / WORKING HYPOTHESES</span><span>NOT A DIAGNOSIS OR VALIDATED SCALE</span></div>
            {exportPackage.cognitiveModel.constructs.length === 0 ? (
              <section className="results-empty">
                <p>NO ANSWERS / NO GUESSING</p>
                <h2>NOTHING<br /><span>INVENTED.</span></h2>
                <strong>You skipped every question, so 3Fold Labs did not assign you a workstyle or generate AI behavior rules.</strong>
                <button type="button" onClick={returnToQuestions}>ANSWER THE QUESTIONS →</button>
              </section>
            ) : <>
              <header className="results-head">
                <p>YOUR FIRST</p><h2>COGNITIVE<br /><span>INTERFACE MAP.</span></h2>
              </header>
              <div className={previewOnly ? 'constructs preview' : 'constructs'}>
              {exportPackage.cognitiveModel.constructs.slice(0, previewOnly ? 1 : undefined).map((construct, index) => {
                const spec = constructSpecs.find(item => item.id === construct.constructId);
                const review = state.reviews[construct.constructId];
                return (
                  <article className="construct" key={construct.constructId}>
                    <div className="construct-top"><span>HYPOTHESIS / {String(index + 1).padStart(2, '0')}</span><b>{Math.round(construct.estimate.confidence * 100)}% ROUTING CONFIDENCE</b></div>
                    <h3>{spec.label}</h3>
                    <p>{spec.definition}</p>
                    <div className="axis"><span>{construct.estimate.lowAnchor}</span><i><b style={{ left: `${(construct.estimate.score + 1) / 2 * 100}%` }} /></i><span>{construct.estimate.highAnchor}</span></div>
                    {!previewOnly && <div className="review-actions">
                      <span>IS THIS ACCURATE ENOUGH TO TRY?</span>
                      <button className={review === 'accurate' ? 'active' : ''} onClick={() => reviewConstruct(construct.constructId, 'accurate')} type="button">YES / TRIAL IT</button>
                      <button className={review === 'correction' ? 'active' : ''} onClick={() => reviewConstruct(construct.constructId, 'correction')} type="button">NO / DEFER IT</button>
                    </div>}
                  </article>
                );
              })}
              </div>

              {previewOnly && <section className="results-gate">
                <div>
                  <span>YOUR FIRST SIGNAL</span>
                  <h3>Your full AI setup is ready.</h3>
                  <p>Create a profile to open the complete map, collaboration behaviors, and portable setup—and keep it available when you come back.</p>
                </div>
                <div className="results-gate-action">
                  <label>
                    <input type="checkbox" checked={consent} onChange={event => changeConsent(event.target.checked)} />
                    <span>Save this result and my answers to my profile.</span>
                  </label>
                  <button type="button" disabled={!consent} onClick={savePreviewAndContinue}>UNLOCK MY FULL RESULTS →</button>
                  {!consent && <small>Nothing is being saved. Check the box only if you want this result attached to your account.</small>}
                </div>
              </section>}

              {!previewOnly && <section className="behavior-plan">
              <header><span>COLLABORATION BEHAVIORS</span><h3>What the AI may change.</h3><p>These actions affect presentation and checking—not the model’s intelligence or your identity.</p></header>
              <div className="behavior-list">
                {exportPackage.adapterPlan.rules.map((rule, index) => (
                  <article key={rule.ruleId} className={rule.status}>
                    <span>0{index + 1}</span><b>{actionNames[rule.mode]}</b><p>{rule.behavior}</p>
                    <button type="button" onClick={() => toggleRuleOverride(rule)}>{rule.status === 'disabled' ? 'ENABLE' : 'DISABLE'}</button>
                    <i>{rule.status.toUpperCase()}</i>
                  </article>
                ))}
              </div>
              </section>}

              {!previewOnly && <footer className="export-dock">
              <div><span>PORTABLE AI SETUP</span><strong>Instructions your AI can use immediately. Raw evidence stays in your full report.</strong></div>
              <button type="button" onClick={copyPackage}>COPY INSTRUCTIONS</button>
              <button type="button" onClick={downloadPackage}>DOWNLOAD AI SETUP</button>
              <button className="install" type="button" onClick={async () => { brainStore.event('portable_copy_clicked'); await copyPackage(); }}>COPY FOR MY AI →</button>
              </footer>}
            </>}
          </section>
        )}
      </main>
      {completing && <CompletionCelebration completed={completed} />}
    </div>
  );
}
